import { ActionType, NetworkActionPrefix } from '../utils/constants';
import {
    DecisionApi, DecisionTopicApi, DecisionTopicCommentApi, DecisionTopicPhaseApi, DecisionTopicTypeApi,
    DecisionTrackingTypeApi, MeetingAdministratorApi, MeetingApi, MeetingInvitationPeriodTypeApi, MeetingMinutesApi, MeetingMinutesContentTypeApi, MeetingParticipantApi,
    MeetingSectionApi, MeetingSectionContentTypeApi, MeetingSectionTextTopicApi, MeetingSectionTopicApi, MeetingSectionTopicAttachmentApi,
    MeetingSectionTopicCommentApi, MeetingSectionTopicLinkApi, MeetingStatusApi, MeetingTemplateAdministratorApi,
    MeetingTemplateApi, MeetingTemplateParticipantApi, MeetingTemplateSectionApi, MeetingTemplateSectionTextTopicApi,
    MeetingTemplateSectionTopicApi, MeetingTemplateSectionTopicLinkApi
} from 'for-straction-service-api';
import {
    doubleNestedEntityLifecycleActionSet, droppableNetworkAPIAction, entityLifecycleActionSet,
    nestedEntityLifecycleActionSet, networkAPIAction, tripleNestedEntityLifecycleActionSet
} from '../utils/actionCreatorsFactory';
import { download } from 'typed/utils';

export const setAgendaFilter = ({ contentFilter, companyFunctionID, targetUserID, virtualTeamID } = {}) => ({
    type: ActionType.SET_AGENDA_FILTER,
    contentFilter,
    companyFunctionID,
    targetUserID,
    virtualTeamID
});

export const loadAllMeetingStatuses = () => networkAPIAction(MeetingStatusApi, 'meetingStatusLoadAllGet', NetworkActionPrefix.LOAD_ALL_MEETING_STATUSES_PREFIX, {});

const decisionActionSet = entityLifecycleActionSet('Decision', 'DECISION', 'decision', DecisionApi, {
    add: true,
    delete: true,
    edit: true,
    loadAll: true,
    load: true
}, true);
export const addDecision = (decision, sync, reloadAfterSync) => decisionActionSet.addDecision(decision, sync, reloadAfterSync);
export const editDecision = (decision, sync) => decisionActionSet.editDecision(decision, sync);
export const loadAllDecisions = () => decisionActionSet.loadAllDecisions();
export const loadDecision = decisionID => decisionActionSet.loadDecision(decisionID);
export const removeDecision = decisionID => decisionActionSet.removeDecision(decisionID);

export const loadAllDecisionTrackingTypes = () => networkAPIAction(DecisionTrackingTypeApi, 'decisionTrackingTypeLoadAllGet', NetworkActionPrefix.LOAD_ALL_DECISION_TRACKING_TYPES_PREFIX);

const decisionTopicActionSet = entityLifecycleActionSet('DecisionTopic', 'DECISION_TOPIC', 'decisionTopic', DecisionTopicApi, {
    delete: true,
    edit: true,
    load: true,
    loadAll: true
}, true);

export const loadDecisionTopic = (decisionTopicID, { maxCommentsCount } = {}) => decisionTopicActionSet.loadDecisionTopic(decisionTopicID, { maxCommentsCount });
export const loadAllDecisionTopics = ({ maxCommentsCount } = {}) => decisionTopicActionSet.loadAllDecisionTopics({ maxCommentsCount });
export const deleteDecisionTopic = decisionTopicID => decisionTopicActionSet.removeDecisionTopic(decisionTopicID);
export const editDecisionTopic = (decisionTopic, sync) => decisionTopicActionSet.editDecisionTopic(decisionTopic, sync);

export const addDecisionTopic = (decisionTopic, sync, reloadTopicsAfterSync) =>
    dispatch => new Promise((resolve, reject) => {
        const payload = {
            decisionTopic,
            decisionTopicID: decisionTopic.ID
        };

        dispatch({ type: ActionType.ADD_DECISION_TOPIC, ...payload });

        if (!sync) {
            resolve();
        }
        else if (!reloadTopicsAfterSync) {
            dispatch(networkAPIAction(DecisionTopicApi, 'decisionTopicAddPut', NetworkActionPrefix.ADD_DECISION_TOPIC_PREFIX, payload, decisionTopic)).then(resolve, reject);
        }
        else {
            dispatch(networkAPIAction(DecisionTopicApi, 'decisionTopicAddPut', NetworkActionPrefix.ADD_DECISION_TOPIC_PREFIX, payload, decisionTopic))
                .then((topicID) => {
                    dispatch(loadAllDecisionTopics());
                    return topicID;
                })
                .then(topicID => resolve(topicID), reject);
        }
    });

export const putDecisionTopicBeforeAnotherTopic = (editedDecisionTopicID, baseDecisionTopicID, reloadAfterSave = false) =>
    dispatch => new Promise((resolve, _reject) => {
        dispatch({ type: ActionType.PUT_DECISION_TOPIC_BEFORE_ANOTHER_TOPIC, editedDecisionTopicID, baseDecisionTopicID });

        return dispatch(networkAPIAction(DecisionTopicApi, 'decisionTopicPutBeforeTopicGet', NetworkActionPrefix.PUT_DECISION_TOPIC_BEFORE_ANOTHER_TOPIC_PREFIX, { editedDecisionTopicID, baseDecisionTopicID }, editedDecisionTopicID, baseDecisionTopicID))
            .then(() => {
                if (reloadAfterSave) {
                    dispatch(loadAllDecisionTopics());
                }
                else {
                    resolve();
                }
            });
    });

export const putDecisionTopicAsLastInPhase = (editedDecisionTopicID, basePhaseID, reloadAfterSave = false) =>
    dispatch => new Promise((resolve, _reject) => {
        dispatch({ type: ActionType.PUT_DECISION_TOPIC_AS_LAST_IN_PHASE, editedDecisionTopicID, basePhaseID });

        return dispatch(networkAPIAction(DecisionTopicApi, 'decisionTopicPutAsLastInPhaseGet', NetworkActionPrefix.PUT_DECISION_TOPIC_AS_LAST_IN_PHASE_PREFIX, { editedDecisionTopicID, basePhaseID }, editedDecisionTopicID, basePhaseID))
            .then(() => {
                if (reloadAfterSave) {
                    dispatch(loadAllDecisionTopics());
                }
                else {
                    resolve();
                }
            });
    });

export const loadDecisionTopicSuggestions = searchString =>
    droppableNetworkAPIAction(DecisionTopicApi, 'decisionTopicSuggestGet', NetworkActionPrefix.LOAD_DECISION_TOPIC_SUGGESTIONS_PREFIX, { searchString }, searchString);

export const resetDecisionTopicSuggestions = () => ({ type: ActionType.RESET_DECISION_TOPIC_SUGGESTIONS });

const decisionTopicCommentActionSet = nestedEntityLifecycleActionSet('DecisionTopicComment', 'DECISION_TOPIC_COMMENT', 'decisionTopic', 'decisionTopicComment', DecisionTopicCommentApi, {
    add: true
});

export const addDecisionTopicComment = (decisionTopicID, decisionTopicComment, sync, reloadAfterSync) => decisionTopicCommentActionSet.addDecisionTopicComment(decisionTopicID, decisionTopicComment, sync, reloadAfterSync);

export const loadDecisionTopicComments = (decisionTopicID, { startID, maxCount, order } = {}) =>
    networkAPIAction(DecisionTopicCommentApi, 'decisionTopicCommentSearchSlicePut', NetworkActionPrefix.LOAD_DECISION_TOPIC_COMMENTS_SLICE_PREFIX, { decisionTopicID }, { TopicID: decisionTopicID }, { startID, maxCount, order });

const decisionTopicPhaseActionSet = entityLifecycleActionSet('DecisionTopicPhase', 'DECISION_TOPIC_PHASE', 'decisionTopicPhase', DecisionTopicPhaseApi, {
    loadAll: true
}, true);
export const addAllDecisionTopicPhases = (decisionTopicPhases, sync) =>
    dispatch => new Promise((resolve, _reject) => {
        dispatch({ type: ActionType.ADD_ALL_DECISION_TOPIC_PHASES, decisionTopicPhases });

        if (!sync) {
            resolve();
        }
        else {
            dispatch(networkAPIAction(DecisionTopicPhaseApi, 'decisionTopicPhaseAddAllPut', NetworkActionPrefix.ADD_ALL_DECISION_TOPIC_PHASES_PREFIX, { decisionTopicPhases }, decisionTopicPhases));
        }
    });
export const editAllDecisionTopicPhases = (decisionTopicPhases, sync) =>
    dispatch => new Promise((resolve, _reject) => {
        dispatch({ type: ActionType.EDIT_ALL_DECISION_TOPIC_PHASES, decisionTopicPhases });

        if (!sync) {
            resolve();
        }
        else {
            dispatch(networkAPIAction(DecisionTopicPhaseApi, 'decisionTopicPhaseEditAllPut', NetworkActionPrefix.EDIT_ALL_DECISION_TOPIC_PHASES_PREFIX, { decisionTopicPhases }, decisionTopicPhases));
        }
    });
export const loadAllDecisionTopicPhases = () => decisionTopicPhaseActionSet.loadAllDecisionTopicPhases();
export const removeAllDecisionTopicPhases = decisionTopicPhaseIDs =>
    networkAPIAction(DecisionTopicPhaseApi, 'decisionTopicPhaseDeleteAllPut', NetworkActionPrefix.DELETE_ALL_DECISION_TOPIC_PHASES_PREFIX, { decisionTopicPhaseIDs }, decisionTopicPhaseIDs);

const decisionTopicTypeActionSet = entityLifecycleActionSet('DecisionTopicType', 'DECISION_TOPIC_TYPE', 'decisionTopicType', DecisionTopicTypeApi, {
    loadAll: true
});
export const addAllDecisionTopicTypes = (decisionTopicTypes, sync) =>
    dispatch => new Promise((resolve, _reject) => {
        dispatch({ type: ActionType.ADD_ALL_DECISION_TOPIC_TYPES, decisionTopicTypes });

        if (!sync) {
            resolve();
        }
        else {
            dispatch(networkAPIAction(DecisionTopicTypeApi, 'decisionTopicTypeAddAllPut', NetworkActionPrefix.ADD_ALL_DECISION_TOPIC_TYPES_PREFIX, { decisionTopicTypes }, decisionTopicTypes));
        }
    });
export const editAllDecisionTopicTypes = (decisionTopicTypes, sync) =>
    dispatch => new Promise((resolve, _reject) => {
        dispatch({ type: ActionType.EDIT_ALL_DECISION_TOPIC_TYPES, decisionTopicTypes });

        if (!sync) {
            resolve();
        }
        else {
            dispatch(networkAPIAction(DecisionTopicTypeApi, 'decisionTopicTypeEditAllPut', NetworkActionPrefix.EDIT_ALL_DECISION_TOPIC_TYPES_PREFIX, { decisionTopicTypes }, decisionTopicTypes));
        }
    });
export const loadAllDecisionTopicTypes = () => decisionTopicTypeActionSet.loadAllDecisionTopicTypes();
export const removeAllDecisionTopicTypes = decisionTopicTypeIDs =>
    networkAPIAction(DecisionTopicTypeApi, 'decisionTopicTypeDeleteAllPut', NetworkActionPrefix.DELETE_ALL_DECISION_TOPIC_TYPES_PREFIX, { decisionTopicTypeIDs }, decisionTopicTypeIDs);


const meetingActionSet = entityLifecycleActionSet('Meeting', 'MEETING', 'meeting', MeetingApi, {
    delete: true,
    edit: true,
    load: true,
    loadAll: true,
    voidAll: true
}, true);
export const deleteMeeting = meetingID => meetingActionSet.removeMeeting(meetingID);
export const editMeeting = (meeting, sync) => meetingActionSet.editMeeting(meeting, sync);
export const loadMeeting = (meetingID, maxSectionsCount = 0) => meetingActionSet.loadMeeting(meetingID, { maxSectionsCount });
export const loadAllMeetings = (maxSectionsCount = 0) => meetingActionSet.loadAllMeetings({ maxSectionsCount });
export const voidAllMeetings = () => meetingActionSet.voidAllMeetings();

export const loadManyMeetings = (meetingIDs, maxSectionsCount = 0) =>
    networkAPIAction(MeetingApi, 'meetingLoadManyPut', NetworkActionPrefix.LOAD_MANY_MEETINGS_PREFIX, { meetingIDs, maxSectionsCount }, meetingIDs, { maxSectionsCount });

export const editAllMeetings = (meetings, sync) =>
    dispatch => new Promise((resolve, reject) => {
        dispatch({ type: ActionType.EDIT_ALL_MEETINGS, meetings });

        if (!sync) {
            resolve();
        }
        else {
            dispatch(networkAPIAction(MeetingApi, 'meetingEditAllPut', NetworkActionPrefix.EDIT_ALL_MEETINGS_PREFIX, { meetings }, meetings))
                .then(resolve, reject);
        }
    });

export const addMeeting = (meeting, startDate, { sourceTemplateID, repeatCount, generationInterval } = {}, sync = false, reloadAfterSync = false) =>
    dispatch => new Promise((resolve, reject) => {
        const payload = { meeting, startDate, templateID: sourceTemplateID, repeatCount, generationInterval };
        dispatch({ type: ActionType.ADD_MEETING, ...payload });

        if (!sync) {
            resolve();
        }
        else {
            const action = networkAPIAction(
                MeetingApi,
                'meetingCreateFromTemplatePut',
                NetworkActionPrefix.ADD_MEETING_PREFIX,
                { payload },
                meeting,
                startDate,
                { sourceTemplateID, repeatCount, generationInterval }
            );

            if (!reloadAfterSync) {
                dispatch(action).then(resolve, reject);
            }
            else {
                dispatch(action)
                    .then((response) => {
                        if (response && response.length === 1) {
                            dispatch(loadMeeting(response.first(), -1));
                        }
                        else {
                            dispatch(loadAllMeetings());
                        }
                    })
                    .then(resolve, reject);
            }
        }
    });

export const cloneMeeting = (meetingID, name, startDate, { repeatCount, generationInterval } = {}) =>
    networkAPIAction(MeetingApi, 'meetingCloneGet', NetworkActionPrefix.CLONE_MEETING_PREFIX, { meetingID, name, startDate }, meetingID, name, startDate, { repeatCount, generationInterval });

export const editMeetingStatus = (meetingID, statusID, sendPDFToParticipants, { coverMessage, recipientFilter } = {}) =>
    networkAPIAction(MeetingApi, 'meetingEditStatusGet', NetworkActionPrefix.EDIT_MEETING_STATUS_PREFIX, { meetingID, statusID, coverMessage, recipientFilter }, meetingID, statusID, sendPDFToParticipants, { coverMessage, recipientFilter });

export const recreateMeetingContentFromTemplate = meetingID =>
    networkAPIAction(MeetingApi, 'meetingRecreateMeetingFromTemplateGet', NetworkActionPrefix.RECREATE_MEETING_CONTENT_PREFIX, { meetingID }, meetingID);

export const addContentToMeeting = (meetingID, sectionID, content) =>
    networkAPIAction(
        MeetingApi,
        'meetingAddContentToMeetingPut',
        NetworkActionPrefix.ADD_CONTENT_TO_MEETING_PREFIX,
        { meetingID, sectionID, content },
        content,
        meetingID,
        sectionID
    );

export const generateMeetingMinutes = meetingID =>
    networkAPIAction(MeetingApi, 'meetingGenerateMeetingMinutesGet', NetworkActionPrefix.GENERATE_MEETING_MINUTES_PREFIX, { meetingID }, meetingID);

export const generateMeetingMinutesPdf = (meetingID, includedContentTypes) =>
    networkAPIAction(MeetingApi, 'meetingGenerateMeetingPdfPut', NetworkActionPrefix.GENERATE_MEETING_MINUTES_PDF_PREFIX, { meetingID, includedContentTypes }, includedContentTypes, meetingID);

export const generateMeetingMinutesWordDoc = (meetingID, includedContentTypes, fileName) =>
    dispatch => new Promise((resolve, reject) => {
        dispatch(networkAPIAction(MeetingApi, 'meetingGenerateMeetingWordDocPut', NetworkActionPrefix.GENERATE_MEETING_MINUTES_WORD_DOC_PREFIX, { meetingID, includedContentTypes }, includedContentTypes, meetingID))
            .then((response) => {
                if (response) {
                    const linkSource = `data:application/pdf;base64,${response}`;
                    download(linkSource, fileName);
                }
            })
            .then(resolve, reject);
    });

export const uploadMeetingMinutes = (meetingID, file) =>
    networkAPIAction(MeetingApi, 'meetingUploadMeetingMinutesPut', NetworkActionPrefix.UPLOAD_MEETING_MINUTES_PREFIX, { meetingID, file }, file, meetingID);

export const deleteMeetingMinutesFile = meetingID =>
    networkAPIAction(MeetingApi, 'meetingDeleteMeetingMinutesFileGet', NetworkActionPrefix.DELETE_MEETING_MINUTES_FILE_PREFIX, { meetingID }, meetingID);

export const downloadMeetingPDF = (meetingID, fileName) =>
    dispatch => new Promise((resolve, reject) => {
        dispatch(networkAPIAction(MeetingApi, 'meetingDownloadMeetingPDFGet', NetworkActionPrefix.DOWNLOAD_MEETING_PDF_PREFIX, { meetingID }, meetingID))
            .then((response) => {
                if (response) {
                    const linkSource = `data:application/pdf;base64,${response}`;
                    download(linkSource, fileName);
                }
            })
            .then(resolve, reject);
    });

export const inviteMeetingMinutesEditor = meetingID =>
    networkAPIAction(MeetingApi, 'meetingInviteMeetingMinutesEditorGet', NetworkActionPrefix.INVITE_MEETING_MINUTES_EDITOR_PREFIX, { meetingID }, meetingID);

export const sendMeetingInvitations = (invitedUsers, meetingID, { coverMessage, includeCalendarEvent } = {}) =>
    networkAPIAction(MeetingApi, 'meetingSendInvitationsPut', NetworkActionPrefix.SEND_MEETING_INVITATIONS_PREFIX, { invitedUsers, meetingID }, invitedUsers, meetingID, { coverMessage, includeCalendarEvent });

export const sendArchivedMeetingPDF = (userIDs, meetingID, { coverMessage } = {}) =>
    networkAPIAction(MeetingApi, 'meetingSendArchivedPDFPut', NetworkActionPrefix.SEND_ARCHIVED_MEETING_PDF_PREFIX, { userIDs, meetingID }, userIDs, meetingID, { coverMessage });

export const sendMeetingMinutes = (userIDs, meetingID) =>
    networkAPIAction(MeetingApi, 'meetingSendMeetingMinutesPut', NetworkActionPrefix.SEND_MEETING_MINUTES_PREFIX, { userIDs, meetingID }, userIDs, meetingID);

export const signMeetingMinutes = (userIDs, meetingID) =>
    networkAPIAction(MeetingApi, 'meetingSignMeetingMinutesPut', NetworkActionPrefix.SIGN_MEETING_MINUTES_PREFIX, { userIDs, meetingID }, userIDs, meetingID);

export const addAllMeetingAdministrators = (meetingID, meetingAdministrators, sync) =>
    dispatch => new Promise((resolve, reject) => {
        dispatch({ type: ActionType.ADD_ALL_MEETING_ADMINISTRATORS, meetingID, meetingAdministrators });

        if (!sync) {
            resolve();
        }
        else {
            dispatch(networkAPIAction(MeetingAdministratorApi, 'meetingAdministratorAddAllPut', NetworkActionPrefix.ADD_ALL_MEETING_ADMINISTRATORS_PREFIX, { meetingID, meetingAdministrators }, meetingAdministrators))
                .then(resolve, reject);
        }
    });
export const removeAllMeetingAdministrators = (meetingID, meetingAdministratorIDs) =>
    networkAPIAction(MeetingAdministratorApi, 'meetingAdministratorDeleteAllPut', NetworkActionPrefix.DELETE_ALL_MEETING_ADMINISTRATORS_PREFIX, { meetingID, meetingAdministratorIDs }, meetingAdministratorIDs);

export const addAllMeetingParticipants = (meetingID, meetingParticipants, sync) =>
    dispatch => new Promise((resolve, reject) => {
        dispatch({ type: ActionType.ADD_ALL_MEETING_PARTICIPANTS, meetingID, meetingParticipants });

        if (!sync) {
            resolve();
        }
        else {
            dispatch(networkAPIAction(MeetingParticipantApi, 'meetingParticipantAddAllPut', NetworkActionPrefix.ADD_ALL_MEETING_PARTICIPANTS_PREFIX, { meetingID, meetingParticipants }, meetingParticipants))
                .then(resolve, reject);
        }
    });
export const removeAllMeetingParticipants = (meetingID, meetingParticipantIDs) =>
    networkAPIAction(MeetingParticipantApi, 'meetingParticipantDeleteAllPut', NetworkActionPrefix.DELETE_ALL_MEETING_PARTICIPANTS_PREFIX, { meetingID, meetingParticipantIDs }, meetingParticipantIDs);

export const touchMeetingParticipant = (meetingID, meetingParticipantID) =>
    networkAPIAction(MeetingParticipantApi, 'meetingParticipantTouchGet', NetworkActionPrefix.TOUCH_MEETING_PARTICIPANT_PREFIX, { meetingID, meetingParticipantID }, meetingParticipantID);

const meetingSectionActionSet = nestedEntityLifecycleActionSet('MeetingSection', 'MEETING_SECTION', 'meeting', 'meetingSection', MeetingSectionApi, {
    add: true,
    delete: true,
    edit: true
}, true);
export const loadAllMeetingSections = meetingID =>
    networkAPIAction(
        MeetingSectionApi,
        'meetingSectionLoadMeetingSectionSliceGet',
        NetworkActionPrefix.LOAD_ALL_MEETING_SECTIONS_PREFIX,
        { meetingID },
        meetingID
    );
export const addMeetingSection = (meetingID, meetingSection, sync) => meetingSectionActionSet.addMeetingSection(meetingID, meetingSection, sync);
export const deleteMeetingSection = (meetingID, meetingSectionID) => meetingSectionActionSet.removeMeetingSection(meetingID, meetingSectionID);
export const editMeetingSection = (meetingID, meetingSection, sync) => meetingSectionActionSet.editMeetingSection(meetingID, meetingSection, sync);

const meetingTemplateActionSet = entityLifecycleActionSet('MeetingTemplate', 'MEETING_TEMPLATE', 'meetingTemplate', MeetingTemplateApi, {
    add: true,
    delete: true,
    edit: true,
    loadAll: true
}, true);
export const addMeetingTemplate = (meetingTemplate, sync) => meetingTemplateActionSet.addMeetingTemplate(meetingTemplate, sync);
export const editMeetingTemplate = (meetingTemplate, sync) => meetingTemplateActionSet.editMeetingTemplate(meetingTemplate, sync);
export const loadAllMeetingTemplates = () => meetingTemplateActionSet.loadAllMeetingTemplates();
export const removeMeetingTemplate = meetingTemplateID => meetingTemplateActionSet.removeMeetingTemplate(meetingTemplateID);

export const saveMeetingReportAsTemplate = (meetingID, deleteSourceMeeting) =>
    networkAPIAction(MeetingTemplateApi, 'meetingTemplateSaveMeetingAsTemplateGet', NetworkActionPrefix.SAVE_MEETING_REPORT_AS_TEMPLATE_PREFIX, { meetingID, deleteSourceMeeting }, meetingID, deleteSourceMeeting);

export const addAllMeetingTemplateAdministrators = (meetingTemplateID, meetingTemplateAdministrators, sync) =>
    dispatch => new Promise((resolve, reject) => {
        dispatch({ type: ActionType.ADD_ALL_MEETING_TEMPLATE_ADMINISTRATORS, meetingTemplateID, meetingTemplateAdministrators });

        if (!sync) {
            resolve();
        }
        else {
            dispatch(networkAPIAction(MeetingTemplateAdministratorApi, 'meetingTemplateAdministratorAddAllPut', NetworkActionPrefix.ADD_ALL_MEETING_TEMPLATE_ADMINISTRATORS_PREFIX, { meetingTemplateID, meetingTemplateAdministrators }, meetingTemplateAdministrators))
                .then(resolve, reject);
        }
    });
export const removeAllMeetingTemplateAdministrators = (meetingTemplateID, meetingTemplateAdministratorIDs) =>
    networkAPIAction(MeetingTemplateAdministratorApi, 'meetingTemplateAdministratorDeleteAllPut', NetworkActionPrefix.DELETE_ALL_MEETING_TEMPLATE_ADMINISTRATORS_PREFIX, { meetingTemplateID, meetingTemplateAdministratorIDs }, meetingTemplateAdministratorIDs);

const meetingTemplateSectionActionSet = nestedEntityLifecycleActionSet('MeetingTemplateSection', 'MEETING_TEMPLATE_SECTION', 'meetingTemplate', 'meetingTemplateSection', MeetingTemplateSectionApi, {
    add: true,
    delete: true,
    edit: true
}, true);
export const addMeetingTemplateSection = (meetingTemplateID, meetingTemplateSection, sync) => meetingTemplateSectionActionSet.addMeetingTemplateSection(meetingTemplateID, meetingTemplateSection, sync);
export const deleteMeetingTemplateSection = (meetingTemplateID, meetingTemplateSectionID) => meetingTemplateSectionActionSet.removeMeetingTemplateSection(meetingTemplateID, meetingTemplateSectionID);
export const editMeetingTemplateSection = (meetingTemplateID, meetingTemplateSection, sync) => meetingTemplateSectionActionSet.editMeetingTemplateSection(meetingTemplateID, meetingTemplateSection, sync);

const meetingTemplateSectionTopicActionSet = doubleNestedEntityLifecycleActionSet('MeetingTemplateSectionTopic', 'MEETING_TEMPLATE_SECTION_TOPIC', 'meetingTemplate', 'meetingTemplateSection', 'meetingTemplateSectionTopic', MeetingTemplateSectionTopicApi, {
    add: true,
    delete: true,
    edit: true,
    load: true
}, true);
export const addMeetingTemplateSectionTopic = (meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopic, sync, reloadAfterSync) =>
    meetingTemplateSectionTopicActionSet.addMeetingTemplateSectionTopic(meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopic, sync, reloadAfterSync);
export const editMeetingTemplateSectionTopic = (meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopic, sync, reloadAfterSync) =>
    meetingTemplateSectionTopicActionSet.editMeetingTemplateSectionTopic(meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopic, sync, reloadAfterSync);
export const loadMeetingTemplateSectionTopic = (meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopicID) =>
    meetingTemplateSectionTopicActionSet.loadMeetingTemplateSectionTopic(meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopicID);
export const removeMeetingTemplateSectionTopic = (meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopicID) =>
    meetingTemplateSectionTopicActionSet.removeMeetingTemplateSectionTopic(meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopicID);

const meetingTemplateSectionTextTopicActionSet = tripleNestedEntityLifecycleActionSet('MeetingTemplateSectionTextTopic', 'MEETING_TEMPLATE_SECTION_TEXT_TOPIC', 'meetingTemplate', 'meetingTemplateSection', 'meetingTemplateSectionTopic', 'meetingTemplateSectionTextTopic', MeetingTemplateSectionTextTopicApi, {
    edit: true
}, true);
export const editMeetingTemplateSectionTextTopic = (meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopicID, meetingTemplateSectionTextTopic, sync) =>
    meetingTemplateSectionTextTopicActionSet.editMeetingTemplateSectionTextTopic(meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopicID, meetingTemplateSectionTextTopic, sync);

const meetingTemplateSectionTopicLinkActionSet = tripleNestedEntityLifecycleActionSet('MeetingTemplateSectionTopicLink', 'MEETING_TEMPLATE_SECTION_TOPIC_LINK', 'meetingTemplate', 'meetingTemplateSection', 'meetingTemplateSectionTopic', 'meetingTemplateSectionTopicLink', MeetingTemplateSectionTopicLinkApi, {
    edit: true
}, true);
export const editMeetingTemplateSectionTopicLink = (meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopicID, meetingTemplateSectionTopicLink, sync) =>
    meetingTemplateSectionTopicLinkActionSet.editMeetingTemplateSectionTopicLink(meetingTemplateID, meetingTemplateSectionID, meetingTemplateSectionTopicID, meetingTemplateSectionTopicLink, sync);

export const addAllMeetingTemplateParticipants = (meetingTemplateID, meetingTemplateParticipants, sync) =>
    dispatch => new Promise((resolve, reject) => {
        dispatch({ type: ActionType.ADD_ALL_MEETING_TEMPLATE_PARTICIPANTS, meetingTemplateID, meetingTemplateParticipants });

        if (!sync) {
            resolve();
        }
        else {
            dispatch(
                networkAPIAction(MeetingTemplateParticipantApi, 'meetingTemplateParticipantAddAllPut', NetworkActionPrefix.ADD_ALL_MEETING_TEMPLATE_PARTICIPANTS_PREFIX, { meetingTemplateID, meetingTemplateParticipants }, meetingTemplateParticipants)
            ).then(resolve, reject);
        }
    });
export const removeAllMeetingTemplateParticipants = (meetingTemplateID, meetingTemplateParticipantIDs) =>
    networkAPIAction(MeetingTemplateParticipantApi, 'meetingTemplateParticipantDeleteAllPut', NetworkActionPrefix.DELETE_ALL_MEETING_TEMPLATE_PARTICIPANTS_PREFIX, { meetingTemplateID, meetingTemplateParticipantIDs }, meetingTemplateParticipantIDs);

export const loadAllMeetingSectionContentTypes = () =>
    networkAPIAction(MeetingSectionContentTypeApi, 'meetingSectionContentTypeLoadAllGet', NetworkActionPrefix.LOAD_ALL_MEETING_SECTION_CONTENT_TYPES_PREFIX, {});

const meetingSectionTopicActionSet = doubleNestedEntityLifecycleActionSet('MeetingSectionTopic', 'MEETING_SECTION_TOPIC', 'meeting', 'meetingSection', 'meetingSectionTopic', MeetingSectionTopicApi, {
    add: true,
    delete: true,
    edit: true,
    load: true
}, true);
export const addMeetingSectionTopic = (meetingID, meetingSectionID, meetingSectionTopic, sync, reloadAfterSync) =>
    meetingSectionTopicActionSet.addMeetingSectionTopic(meetingID, meetingSectionID, meetingSectionTopic, sync, reloadAfterSync);
export const editMeetingSectionTopic = (meetingID, meetingSectionID, meetingSectionTopic, sync, reloadAfterSync) =>
    meetingSectionTopicActionSet.editMeetingSectionTopic(meetingID, meetingSectionID, meetingSectionTopic, sync, reloadAfterSync);
export const loadMeetingSectionTopic = (meetingID, meetingSectionID, meetingSectionTopicID) =>
    meetingSectionTopicActionSet.loadMeetingSectionTopic(meetingID, meetingSectionID, meetingSectionTopicID);
export const removeMeetingSectionTopic = (meetingID, meetingSectionID, meetingSectionTopicID) => meetingSectionTopicActionSet.removeMeetingSectionTopic(meetingID, meetingSectionID, meetingSectionTopicID);

export const moveMeetingSectionTopicToAnotherMeeting = (meetingSectionTopic, sourceMeetingID, targetMeetingID) =>
    dispatch => new Promise((resolve, reject) => {
        dispatch(networkAPIAction(MeetingSectionTopicApi, 'meetingSectionTopicEditPut', NetworkActionPrefix.MOVE_MEETING_SECTION_TOPIC_TO_ANOTHER_MEETING_PREFIX, { meetingSectionTopic, sourceMeetingID, targetMeetingID }, meetingSectionTopic))
            .then(() => {
                dispatch(loadMeeting(targetMeetingID));
            })
            .then(resolve, reject);
    });

export const unlinkMeetingSectionTopicContent = (meetingID, meetingSectionID, meetingSectionTopicID) =>
    networkAPIAction(MeetingSectionTopicApi, 'meetingSectionTopicUnlinkContentGet', NetworkActionPrefix.UNLINK_MEETING_SECTION_TOPIC_CONTENT_PREFIX, { meetingID, meetingSectionID, meetingSectionTopicID }, meetingSectionTopicID);

const meetingSectionTextTopicActionSet = tripleNestedEntityLifecycleActionSet('MeetingSectionTextTopic', 'MEETING_SECTION_TEXT_TOPIC', 'meeting', 'meetingSection', 'meetingSectionTopic', 'meetingSectionTextTopic', MeetingSectionTextTopicApi, {
    edit: true
}, true);
export const editMeetingSectionTextTopic = (meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTextTopic, sync) =>
    meetingSectionTextTopicActionSet.editMeetingSectionTextTopic(meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTextTopic, sync);

const meetingSectionTopicLinkActionSet = tripleNestedEntityLifecycleActionSet('MeetingSectionTopicLink', 'MEETING_SECTION_TOPIC_LINK', 'meeting', 'meetingSection', 'meetingSectionTopic', 'meetingSectionTopicLink', MeetingSectionTopicLinkApi, {
    edit: true
}, true);
export const editMeetingSectionTopicLink = (meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicLink, sync) =>
    meetingSectionTopicLinkActionSet.editMeetingSectionTopicLink(meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicLink, sync);

export const loadAllMeetingInvitationPeriodTypes = () =>
    networkAPIAction(MeetingInvitationPeriodTypeApi, 'meetingInvitationPeriodTypeLoadAllGet', NetworkActionPrefix.LOAD_ALL_MEETING_INVITATION_PERIOD_TYPES_PREFIX, {});

const meetingSectionTopicAttachmentActionSet = tripleNestedEntityLifecycleActionSet('MeetingSectionTopicAttachment', 'MEETING_SECTION_TOPIC_ATTACHMENT', 'meeting', 'meetingSection', 'meetingSectionTopic', 'meetingSectionTopicAttachment', MeetingSectionTopicAttachmentApi, {
    add: true,
    edit: true,
    delete: true,
    load: true
}, true);
export const addMeetingSectionTopicAttachment = (meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicAttachment, sync, reloadAfterSync) =>
    meetingSectionTopicAttachmentActionSet.addMeetingSectionTopicAttachment(meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicAttachment, sync, reloadAfterSync);
export const editMeetingSectionTopicAttachment = (meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicAttachment, sync) =>
    meetingSectionTopicAttachmentActionSet.editMeetingSectionTopicAttachment(meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicAttachment, sync);
export const loadMeetingSectionTopicAttachment = (meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicAttachmentID) =>
    meetingSectionTopicAttachmentActionSet.loadMeetingSectionTopicAttachment(meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicAttachmentID);
export const removeMeetingSectionTopicAttachment = (meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicAttachmentID) =>
    meetingSectionTopicAttachmentActionSet.removeMeetingSectionTopicAttachment(meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicAttachmentID);

export const downloadMeetingSectionTopicAttachment = (meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicAttachmentID, fileName, mimeType) =>
    dispatch => new Promise((resolve, reject) => {
        dispatch(networkAPIAction(MeetingSectionTopicAttachmentApi, 'meetingSectionTopicAttachmentDownloadAttachmentGet', NetworkActionPrefix.DOWNLOAD_MEETING_SECTION_TOPIC_ATTACHMENT_PREFIX, { meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicAttachmentID }, meetingSectionTopicAttachmentID))
            .then((response) => {
                if (response) {
                    const linkSource = `data:${mimeType};base64,${response}`,
                        linkElement = document.createElement('a');

                    linkElement.setAttribute('href', linkSource);
                    linkElement.setAttribute('download', fileName);
                    linkElement.click();
                }
            })
            .then(resolve, reject);
    });

const meetingSectionTopicCommentActionSet = tripleNestedEntityLifecycleActionSet('MeetingSectionTopicComment', 'MEETING_SECTION_TOPIC_COMMENT', 'meeting', 'meetingSection', 'meetingSectionTopic', 'meetingSectionTopicComment', MeetingSectionTopicCommentApi, {
    add: true,
    edit: true
}, true);
export const addMeetingSectionTopicComment = (meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicComment, sync) =>
    meetingSectionTopicCommentActionSet.addMeetingSectionTopicComment(meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicComment, sync);
export const editMeetingSectionTopicComment = (meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicComment, sync) =>
    meetingSectionTopicCommentActionSet.editMeetingSectionTopicComment(meetingID, meetingSectionID, meetingSectionTopicID, meetingSectionTopicComment, sync);
export const loadMeetingSectionTopicCommentSlice = (meetingID, meetingSectionID, meetingSectionTopicID, { startID, maxCount, slicingOrder } = {}) =>
    networkAPIAction(MeetingSectionTopicCommentApi, 'meetingSectionTopicCommentSearchSlicePut', NetworkActionPrefix.LOAD_MEETING_SECTION_TOPIC_COMMENTS_SLICE_PREFIX, { meetingID, meetingSectionID, meetingSectionTopicID, startID, maxCount, slicingOrder }, { SectionTopicID: meetingSectionTopicID }, { startID, maxCount, slicingOrder });

export const dropAgendaNavigationHistory = () => ({ type: ActionType.DROP_AGENDA_NAVIGATION_HISTORY });

const meetingMinutesActionSet = entityLifecycleActionSet('MeetingMinutes', 'MEETING_MINUTES', 'meetingMinutes', MeetingMinutesApi, {
    delete: true,
    load: true,
    loadAll: true
}, true);
export const loadAllMeetingMinutes = () => meetingMinutesActionSet.loadAllMeetingMinuteses();
export const loadMeetingMinutes = meetingMinutesID => meetingMinutesActionSet.loadMeetingMinutes(meetingMinutesID);
export const removeMeetingMinutes = meetingMinutesID => meetingMinutesActionSet.removeMeetingMinutes(meetingMinutesID);

export const loadAllMeetingMinutesContentTypes = () =>
    networkAPIAction(MeetingMinutesContentTypeApi, 'meetingMinutesContentTypeLoadAllGet', NetworkActionPrefix.LOAD_ALL_MEETING_MINUTES_CONTENT_TYPES_PREFIX, {});
